// Customer selects a tip percentage

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Lock, ChevronLeft, Edit2 } from 'lucide-react';

// Format currency helper with thousand separators
const formatCurrency = (amount: number, moneda: string = 'DOP') => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
  return `$${formatted} ${moneda}`
}

// Header Component
const Header: React.FC<{ title: string; onBack?: () => void }> = ({ title, onBack }) => {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
      <div className="max-w-md mx-auto px-4 h-12 flex items-center justify-between">
        {onBack ? (
          <button 
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/50 active:scale-95 transition-all text-gray-900"
          >
            <ChevronLeft size={22} />
          </button>
        ) : (
          <div />
        )}
        
        <h1 className="text-sm font-semibold text-gray-900 tracking-tight absolute left-1/2 -translate-x-1/2">
          {title}
        </h1>

        <div />
      </div>
    </header>
  )
}

// TipSelector Component
interface TipSelectorProps {
  subtotal: number;
  selectedTip: number;
  onSelect: (percentage: number) => void;
  moneda: string;
  customTipAmount: number;
  onCustomTipChange: (amount: number) => void;
}

const TipSelector: React.FC<TipSelectorProps> = ({ subtotal, selectedTip, onSelect, moneda, customTipAmount, onCustomTipChange }) => {
  const tips = [10, 15, 20];
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInput, setCustomInput] = useState('');
  
  useEffect(() => {
    if (selectedTip === -1 && customTipAmount > 0) {
      setCustomInput(customTipAmount.toString());
      setShowCustomInput(true);
    }
  }, [selectedTip, customTipAmount]);

  const handleCustomClick = () => {
    setShowCustomInput(true);
    onSelect(-1);
  };

  const handleCustomSubmit = () => {
    const amount = parseFloat(customInput) || 0;
    onCustomTipChange(amount);
    setShowCustomInput(false);
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-right-8 duration-300">
      <div className="text-center py-4">
        <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">Subtotal</p>
        <p className="text-2xl font-bold text-gray-900 tracking-tight">{formatCurrency(subtotal, moneda)}</p>
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {tips.map((tip) => {
          const isSelected = selectedTip === tip;
          return (
            <button
              key={tip}
              onClick={() => {
                onSelect(tip);
                setShowCustomInput(false);
              }}
              className={`
                relative p-2.5 rounded-xl border-2 transition-all duration-300 flex items-center justify-center
                ${isSelected 
                  ? 'border-gray-900 bg-gray-900 text-white shadow-lg scale-[1.02]' 
                  : 'border-transparent bg-white text-gray-900 shadow-sm hover:scale-[1.01]'}
              `}
            >
              <span className={`text-[13px] font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>{tip}%</span>
            </button>
          )
        })}
      </div>
      
      {showCustomInput ? (
        <div className="bg-white rounded-xl border-2 border-gray-900 p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-semibold text-gray-900">Personalizada</span>
            <button
              onClick={() => {
                setShowCustomInput(false);
                onSelect(0);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="text-[11px]">Cerrar</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500">$</span>
            <input
              type="number"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="0.00"
              className="flex-1 text-[13px] font-medium text-gray-900 bg-transparent border-none outline-none"
              autoFocus
            />
            <span className="text-[11px] text-gray-500">{moneda}</span>
          </div>
          <button
            onClick={handleCustomSubmit}
            className="w-full mt-2 py-2 bg-gray-900 text-white rounded-lg text-[11px] font-semibold hover:bg-gray-800 transition-colors"
          >
            Aplicar
          </button>
        </div>
      ) : (
        <button
          onClick={handleCustomClick}
          className={`
            w-full p-3 rounded-xl border-2 transition-all duration-300 flex items-center justify-between px-4
            ${selectedTip === -1
              ? 'border-gray-900 bg-white text-gray-900' 
              : 'border-transparent bg-white text-gray-500 shadow-sm'}
          `}
        >
          <span className="font-semibold text-[13px]">Personalizada</span>
          <Edit2 size={14} className="text-gray-400" />
        </button>
      )}
    </div>
  );
};

const TipSelectionScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mesaId, restaurantId, waiterName = 'tu mesero', staffId, amount, moneda = 'DOP' } = location.state || {};

  const [selectedTip, setSelectedTip] = useState<number>(0);
  const [customTipAmount, setCustomTipAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculate tip amount
  const tipAmount = selectedTip === -1 
    ? customTipAmount 
    : selectedTip > 0 
      ? amount * (selectedTip / 100)
      : 0;
  
  const totalWithTip = amount + tipAmount;
  const hasTip = selectedTip > 0 || (selectedTip === -1 && customTipAmount > 0);

  const handleContinue = () => {
    setIsProcessing(true);
    
    // Navigate to payment breakdown screen
    navigate('/payment-breakdown', {
      state: {
        mesaId,
        restaurantId,
        waiterName,
        staffId,
        subtotal: amount,
        tipPercentage: selectedTip === -1 ? 0 : selectedTip,
        tipAmount,
        total: totalWithTip
      }
    });
  };

  const handleContinueWithoutTip = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsProcessing(true);
    const existingState = (location.state || {}) as Record<string, unknown>;
    navigate('/payment-breakdown', {
      state: {
        ...existingState,
        mesaId,
        restaurantId,
        waiterName,
        staffId,
        subtotal: amount,
        tipPercentage: 0,
        tipAmount: 0,
        total: amount
      }
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] animate-in fade-in duration-300">
      <Header title="Seleccionar Propina" onBack={handleBack} />

      <main className="max-w-md mx-auto px-4 py-3 space-y-3">
        <TipSelector 
          subtotal={amount}
          selectedTip={selectedTip}
          onSelect={setSelectedTip}
          moneda={moneda}
          customTipAmount={customTipAmount}
          onCustomTipChange={setCustomTipAmount}
        />
      </main>

      {/* Premium Floating Bottom Bar */}
      <div className="sticky bottom-0 left-0 right-0 p-3 pb-4 z-40 bg-[#F2F2F7] pt-2 space-y-2">
        <div className="max-w-md mx-auto space-y-2">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isProcessing || !hasTip}
            className={`
              w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15
              flex items-center justify-between px-4 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
              overflow-hidden group
              ${hasTip && !isProcessing
                ? 'bg-[#1C1C1E] text-white hover:scale-[1.01] active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed shadow-none'}
            `}
          >
            {/* Background Gradient Shine */}
            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] ${!hasTip || isProcessing ? 'hidden' : ''}`} />

            {isProcessing ? (
              <div className="w-full flex justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2.5">
                  <div className="bg-white/10 p-1 rounded-lg">
                    <Lock size={14} className={hasTip ? "text-white" : "text-gray-400"} />
                  </div>
                  <span className="text-[13px] font-medium tracking-wide">
                    Continuar
                  </span>
                </div>
                
                <div className="flex items-center gap-2.5">
                  <span className="text-[15px] font-bold tracking-tight">
                    {formatCurrency(totalWithTip, moneda)}
                  </span>
                  <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 transition-transform" />
                </div>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleContinueWithoutTip}
            disabled={isProcessing}
            className={`
              w-full h-12 rounded-xl font-semibold
              flex items-center justify-center transition-all duration-300
              ${!isProcessing
                ? 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed'}
            `}
          >
            <span className="text-[13px] font-medium">Continuar sin propina</span>
          </button>
        </div>
      </div>
      
      {/* Global CSS for shimmer */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-from-right-8 {
          from { transform: translateX(2rem); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-in {
          animation: fade-in 0.3s ease-out;
        }
        .fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .slide-in-from-right-8 {
          animation: slide-in-from-right-8 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TipSelectionScreen;
